<template>
	<div>
		<div class="row mb-2">
			<div class="col-7">
				<h1>{{$t('search-timeline')}}</h1>
			</div>
			<div class="col-5">
				<button
                    type="button"
                    class="btn btn-secondary btn-lg mr-2 mb-3 float-right"
                    v-if="showDownloadButton && $UserSession.hasPriv.call(this, 'TIMELINE_DOWNLOAD')"
                    @click="downloadTimeline"
                    tabindex="0">
                    <b-icon icon="download"></b-icon>
                </button>
			</div>
		</div>

        <div class="row align-items-center" v-if="$UserSession.hasPriv.call(this, 'TIMELINE_VIEW_ALL')">
            <div class="col-md-10 col-10">
                <SelectSearchInput v-if="!(student || submitting)"
                    :label="$t('student')"
                    v-model="filters.user_id"
                    :options="students"
                    optionLabel="displayName"
                    optionValue="id">
                </SelectSearchInput>
                <div v-if="submitting && !student" class="skeleton midnight h32 my-auto"></div>
                <Avatar v-if="student"
                    class="avatar float-left mr-4 mb-3 lg:mb-0 ml-2"
                    :userId="student.id" />
                <div class="mr-2 mb-0" v-if="student">
                    <p class="mb-0"><strong>{{ student.displayName }}</strong></p>
                    <p class="mb-0">
                        {{ `${$t('year-colon')} ${student.year}, ${$t('programme-colon')} ${student.programme}` }}
                    </p>
                </div>
            </div>
            <div class="col-md-2 col-2">
                <button
                    type="button"
                    class="btn btn-primary ml-2 mt-3 float-right"
                    v-if="!(student || submitting)"
                    @click="getStudentTimeline()"
                    :disabled="!filters.user_id">
                        {{ $t('search') }}
                </button>
                <button
                    type="button"
                    class="btn btn-outline-primary ml-2 mt-3 float-right"
                    v-if="!submitting && student"
                    @click="changeStudent()"
                    :disabled="!filters.user_id">
                        {{ $t('change') }}
                </button>
            </div>
        </div>

        <div class="row" v-if="activities_ready">
            <div class="col-md-6 col-12">
                <TextInput
                    :label="$t('search')"
                    v-model="filters.search"
                    @input="value => refresh({ search: value.trim() })">
                </TextInput>
            </div>
            <div class="col-md-6 col-12">
                <div class="form-group">
                    <label>{{ $t('tags') }}</label>
                    <div class="pseudo-input mb-3">
                        <input
                            type="search"
                            class="input-bare w-full flex-1"
                            :placeholder="$t('tags')"
                            @click="selectItem('tag')"
                            @keyup.enter="selectItem('tag')" />
                        <span
                            class="badge badge-secondary badge-sm mr-2 mb-2"
                            v-for="(tag, index) in filters.tags"
                            :key="index">
                            {{ tag.title }}
                            <a @click="removeItem(tag, 'tag')">
                                <b-icon icon="x"></b-icon>
                            </a>
                        </span>
                        <span
                            class="badge badge-primary badge-sm mr-2 mb-2"
                            v-if="filters.tags.length"
                            @click="selectItem('tag')"
                            @keyup.enter="selectItem('tag')"
                            :aria-label="$t('tag') + ' ' + $t('add')"
                            tabindex="0">
                            <b-icon icon="plus"></b-icon> {{ $t("add") }}
                        </span>
                        <span
                            class="badge badge-danger badge-sm mr-2 mb-2"
                            v-if="filters.tags.length"
                            @click="filters.tags = []; refresh({ tags: filters.tags })"
                            @keyup.enter="filters.tags = []; refresh({ tags: filters.tags })"
                            :aria-label="$t('tag') + ' ' + $t('clear-all')"
                            tabindex="0">
                            <b-icon icon="x"></b-icon> {{ $t('clear-all') }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="col-md-6 col-12">
                <div class="form-group">
                    <label>{{ $t('procedures') }}</label>
                    <div class="pseudo-input mb-3">
                        <input
                            type="search"
                            :placeholder="$t('procedures')"
                            class="input-bare w-full flex-1"
                            @click="selectItem('procedure')"
                            @keyup.enter="selectItem('procedure')" />
                        <span
                            class="badge badge-secondary badge-sm mr-2 mb-2"
                            v-for="(procedure, index) in filters.procedures"
                            :key="index">
                            {{ procedure.title }}
                            <a @click="removeItem(procedure, 'procedure')">
                                <b-icon icon="x"></b-icon>
                            </a>
                        </span>
                        <span
                            class="badge badge-primary badge-sm mr-2 mb-2"
                            v-if="filters.procedures.length"
                            @click="selectItem('procedure')"
                            @keyup.enter="selectItem('procedure')"
                            :aria-label="$t('procedure') + ' ' + $t('add')"
                            tabindex="0">
                            <b-icon icon="plus"></b-icon> {{ $t("add") }}
                        </span>
                        <span
                            class="badge badge-danger badge-sm mr-2 mb-2"
                            v-if="filters.procedures.length"
                            @click="filters.procedures = []; refresh({ procedures: filters.procedures })"
                            @keyup.enter="filters.procedures = []; refresh({ procedures: filters.procedures })"
                            :aria-label="$t('procedure') + ' ' + $t('clear-all')"
                            tabindex="0">
                            <b-icon icon="x"></b-icon> {{ $t('clear-all') }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="col-md-6 col-12" v-if="$UserSession.hasPriv.call(this, 'TIMELINE_VIEW_ALL')">
                <div class="form-group">
                    <label>{{ $t('workflows') }}</label>
                    <div class="pseudo-input mb-3">
                        <input
                            type="search"
                            class="input-bare w-full flex-1"
                            :placeholder="$t('workflows')"
                            @click="selectItem('workflow')"
                            @keyup.enter="selectItem('workflow')" />
                        <span
                            class="badge badge-secondary badge-sm mr-2 mb-2"
                            v-for="(workflow, index) in filters.workflows"
                            :key="index">
                            {{ workflow.title }}
                            <a @click="removeItem(workflow, 'workflow')">
                                <b-icon icon="x"></b-icon>
                            </a>
                        </span>
                        <span
                            class="badge badge-primary badge-sm mr-2 mb-2"
                            v-if="filters.workflows.length"
                            @click="selectItem('workflow')"
                            @keyup.enter="selectItem('workflow')"
                            :aria-label="$t('workflow') + ' ' + $t('add')"
                            tabindex="0">
                            <b-icon icon="plus"></b-icon> {{ $t("add") }}
                        </span>
                        <span
                            class="badge badge-danger badge-sm mr-2 mb-2"
                            v-if="filters.workflows.length"
                            @click="filters.workflows = []; refresh({ workflows: filters.workflows })"
                            @keyup.enter="filters.workflows = []; refresh({ workflows: filters.workflows })"
                            :aria-label="$t('workflow') + ' ' + $t('clear-all')"
                            tabindex="0">
                            <b-icon icon="x"></b-icon> {{ $t('clear-all') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

		<!-- Date Range Filter Row with automatic refresh -->
        <div class="row align-items-end" v-if="activities_ready">
            <div class="col-md-4 col-12">
                <div class="form-group">
                <label>{{ $t('date-from') }}</label>
                <input type="date" v-model="dateFrom" class="form-control" @change="refreshDateFilter" />
                </div>
            </div>
            <div class="col-md-4 col-12">
                <div class="form-group">
                <label>{{ $t('date-to') }}</label>
                <input type="date" v-model="dateTo" class="form-control" :disabled="!dateFrom" :min="dateFrom" @change="refreshDateFilter" />
                </div>
            </div>
            <div class="col-md-4 col-12">
                <div class="form-group">
                <!-- Dummy label to match vertical spacing -->
                <label style="visibility: hidden;">Reset</label>
                <button type="button" class="btn btn-secondary w-100" @click="resetFilters">
                    {{ $t('reset-filters') }}
                </button>
                </div>
            </div>
        </div>

		<div class="row">
			<div class="col-md-12" v-if="student || (isStudent && filters.user_id)">
				<template v-if="activities_ready">
					<h2 class="mb-2">{{$t('recent-activity')}}</h2>
					<!-- Activity Log -->
					<ScrollPaginator
						:data="activities"
						:submitting="submitting"
						:filters="filters"
						scrollElementId="main"
						@toPage="toPage">
						<template v-slot:item="{ item: activity }">
							<LogItem :activity="activity" @click="$router.push(`/reflections/view/${activity.id}`)" />
						</template>
					</ScrollPaginator>
 				</template>
 				<template v-else>
 					<div class="skeleton midnight h32 mb-2"></div>
 					<div class="skeleton midnight h32 mb-2"></div>
 					<div class="skeleton midnight h32 mb-2"></div>
 					<div class="skeleton midnight h32 mb-2"></div>
 					<div class="skeleton midnight h32 mb-2"></div>
 				</template>
			</div>
		</div>

	</div>
</template>

<script>
import ScrollPaginator from "src/components/Table/ScrollPaginator";
import LogItem from "src/components/Reflections/LogItem.vue";
import Avatar from "src/components/Avatar.vue";

export default {
	components: {
		Avatar,
		ScrollPaginator,
		LogItem
	},
	data() {
		return {
			searchTerm: null,
			activities_ready: false,
			activities: [],
			submitting: false,
			// The filters sent to the API (will include the datetime strings with times)
			filters: {
				page: 1,
				page_size: 10,
				total: 1,
				user_id: null,
				workflows: [],
				tags: [],
				procedures: [],
				datetime_from: '',
				datetime_to: '',
				search: ''
			},
            students: [],
            student: null,
            procedures: [],
            workflows: [],
            tags: [],
            // New properties for the date input values (plain "YYYY-MM-DD")
            dateFrom: '',
            dateTo: ''
		};
	},
	mounted() {
		//this.listActivities();
        this.listWorkflows();
        this.listTags();
        this.listProcedures();
        this.getStudents();
	},
    created() {
        if (this.isStudent) {
            this.filters.user_id = this.userID;
            this.listActivities();
        }
    },
	methods: {
		listActivities() {
            if (this.submitting) return;
            this.submitting = true;
			this.$cfetch(`${this.$settings.APIBaseURL}/activitylog/list`, {
				method: 'POST',
				data: this.filters,
			}).then(res => {
				this.submitting = false;
                if (res.status !== 'SUCCESS') return;
                this.activities = res.results;
                this.filters.page = res.pagination.page;
                this.filters.page_size = res.pagination.page_size;
                this.filters.total = res.pagination.total;
                this.activities_ready = true;
			}).catch(err => {
				this.$log.error.call(this, err)
			})
		},
        downloadTimeline() {
            const userId = this.isStudent ? this.userID : this.filters.user_id;
            const body = {
                username: this.$UserSession.getValue("username"),
                authToken: this.$UserSession.getValue("authToken"),
                user_id: userId,
                download: "pdf",
                filters: JSON.stringify(this.filters) // Pass full filter object
            };
            const url = `${this.$settings.APIBaseURL}/timeline/download?` + 
                new URLSearchParams(body).toString();
            window.open(url);
        },
        refreshDateFilter() {
            const from = this.dateFrom ? this.dateFrom + " 00:00:00" : "";
            const to = this.dateTo ? this.dateTo + " 23:59:59" : "";
            this.refresh({
                datetime_from: from,
                datetime_to: to
            });
        },
        selectItem(type) {
			const self = this;
            let arr = [];
            let title = '';
            let refreshObj = {};
            let data = [];
            // Start data selector
            if (type == 'workflow') {
                arr = self.filters.workflows;
                title = this.$t("select-workflows");
                refreshObj = { workflows: self.filters.workflows };
                data = this.workflows;
            } else if (type == 'tag') {
                arr = self.filters.tags;
                title = this.$t("select-tags");
                refreshObj = { tags: self.filters.tags };
                data = this.tags;
            } else {
                arr = self.filters.procedures;
                title = this.$t("select-procedures");
                refreshObj = { procedures: self.filters.procedures };
                data = this.procedures;
            }
			this.$store.commit("openModal", {
				type: "selector",
				title: title,
				selectCallback: function (selection) {
					const isSelected = arr.find((item, i) => {
						if (item.id == selection.id) {
							self.$delete(arr, i);
                            self.refresh(refreshObj);
							return true;
						}
					});
					if (!isSelected) {
						arr.push(selection);
                        self.refresh(refreshObj);
					}
				},
				selected: arr,
				data: data,
				captionKey: "title",
				selectAllBtn: true
			});
		},
        removeItem(selection, type) {
			const self = this;
            let arr = [];
            let refreshObj = {};
            if (type == 'workflow') {
                arr = self.filters.workflows;
                refreshObj = { workflows: self.filters.workflows };
            } else if (type == 'tag') {
                arr = self.filters.tags;
                refreshObj = { tags: self.filters.tags };
            } else {
                arr = self.filters.procedures;
                refreshObj = { procedures: self.filters.procedures };
            }
			arr.find((item, i) => {
				if (item.id === selection.id) {
					self.$delete(arr, i);
                    self.refresh(refreshObj);
					return true;
				}
			});
			return false;
		},
        listWorkflows() {
            this.$cfetch(`${this.$settings.APIBaseURL}/workflows/list`, {
				method: 'POST',
				data: {
                    ...this.filters,
                    just_active: true // Remove or set to false if you want to display inactive WF
                },
			}).then(res => {
                if (res.status !== 'SUCCESS') return;
                this.workflows = res.results;
			}).catch(err => {
				this.$log.error.call(this, err);
			});
        },
        listTags() {
            this.$cfetch(`${this.$settings.APIBaseURL}/tags/list`, {
				method: 'POST',
				data: {},
			}).then(res => {
                if (res.status !== 'SUCCESS') return;
                this.tags = res.results.map(tag => {
                    return {
                        id: tag.id,
                        title: tag.name
                    };
                });
			}).catch(err => {
				this.$log.error.call(this, err);
				console.error(err);
			});
        },
        listProcedures() {
            this.$cfetch(`${this.$settings.APIBaseURL}/procedures/list`, {
				method: 'POST',
				data: {
                    see_all: true,
                    published: true
                },
			}).then(res => {
                if (res.status !== 'SUCCESS') return;
                this.procedures = res.results.map(item => {
                    return {
                        id: item.id,
                        title: item.caption
                    };
                });
			}).catch(err => {
				this.$log.error.call(this, err);
				console.error(err);
			});
        },
        toPage(pageNo) {
            this.filters.page = pageNo;
            this.listActivities();
        },
        getStudents() {
            this.submitting = true;
            this.$users.getStudents.call(this).then(users => {
                this.students = users;
                this.submitting = false;
            });
        },
        getStudentTimeline() {
            this.student = this.students.filter(user => user.id === this.filters.user_id)[0];
            this.listActivities();
        },
        changeStudent() {
            this.student = null;
            this.filters.user_id = null;
            this.activities_ready = false;
            this.activities = [];
        },
        refresh(filters) {
            this.filters = {
                ...this.filters,
                ...filters,
                page: 1,
                total: 1
            };
            this.listActivities();
        },
        resetFilters() {
            const currentUser = this.isStudent ? this.userID : this.filters.user_id;
            // Reset the API filters as well as the date input properties
            this.filters = {
                page: 1,
                page_size: 10,
                total: 1,
                user_id: currentUser,
                workflows: [],
                tags: [],
                procedures: [],
                datetime_from: '',
                datetime_to: '',
                search: ''
            };
            this.dateFrom = '';
            this.dateTo = '';
            this.listActivities();
        }
    },
	computed: {
		isStudent() {
			return this.$UserSession.hasPriv.call(this, "TIMELINE_VIEW_OWN")
                && !this.$UserSession.hasPriv.call(this, "ALL");
		},
        userID() {
            return this.$UserSession.getValue.call(this, 'id');
        },
        showDownloadButton() {
            return (this.isStudent || (!!this.student && this.filters.user_id))
                && this.$UserSession.hasPriv.call(this, "TIMELINE_DOWNLOAD");
        }   
	},
}
</script>
